/**
 * Tela "Meu Perfil" — acessível a qualquer perfil autenticado.
 *
 * Permite ao próprio usuário completar ou alterar seus dados de contato (nome, sobrenome, cargo,
 * telefone e foto) sem depender de um administrador. Isso resolve o Defeito 2 do bug de publicação:
 * um vendedor com telefone vazio não ficava mais preso sem saída.
 *
 * Campos de privilégio (perfil de acesso, trilha) são apenas leitura aqui, com explicação de que só
 * um administrador altera.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  aplicarMascaraTelefoneBr,
  exibicaoParaCampo,
  normalizarTelefoneBr,
  validarFoto,
  type FotoCandidata,
  type ProblemaFoto,
} from '@emaster/dominio';

import { useAutenticacao } from '../autenticacao/contexto.tsx';
import { atualizarMeuPerfil, meuPerfil } from '../api/cliente.ts';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface DadosPerfil {
  sub: string;
  email: string | null;
  perfil: string | null;
  nome: string | null;
  sobrenome: string | null;
  cargo: string | null;
  telefone: string | null;
  whatsapp: string | null;
  fotoUrl: string | null;
  cadastroCompleto: boolean;
  trilhas: readonly string[];
}

interface DadosFormulario {
  nome: string;
  sobrenome: string;
  cargo: string;
  telefone: string;
  fotoPreview: string | null;
  fotoBase64: string | null;
  fotoRemovida: boolean;
}

// ---------------------------------------------------------------------------
// Cargos sugeridos — mesma lista de TelaUsuarios.tsx
// ---------------------------------------------------------------------------

const CARGOS_SUGERIDOS = [
  'Inside Sales',
  'Account Manager',
  'Public Sector Account Manager',
  'Sales Director',
  'AWS Solution Advisor',
  'Customer Success',
  'Pre-Sales Engineer',
  'Diretoria eMaster',
];

// ---------------------------------------------------------------------------
// Redimensionamento de foto (reproduz o padrão de TelaUsuarios.tsx)
// ---------------------------------------------------------------------------

async function redimensionar(arquivo: File): Promise<string> {
  const bitmap = await createImageBitmap(arquivo);
  const lado = 256;
  const canvas = document.createElement('canvas');
  canvas.width = lado;
  canvas.height = lado;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Não foi possível processar a imagem.');
  const menor = Math.min(bitmap.width, bitmap.height);
  const sx = (bitmap.width - menor) / 2;
  const sy = (bitmap.height - menor) / 2;
  ctx.drawImage(bitmap, sx, sy, menor, menor, 0, 0, lado, lado);
  return canvas.toDataURL('image/png');
}

// ---------------------------------------------------------------------------
// Rótulos de perfil
// ---------------------------------------------------------------------------

const NOME_PERFIL: Readonly<Record<string, string>> = {
  admin: 'Administrador',
  diretor: 'Diretor',
  vendedor: 'Vendedor',
  leitor: 'Leitor',
};

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function TelaMeuPerfil() {
  const { sessao } = useAutenticacao();

  const [perfil, setPerfil] = useState<DadosPerfil | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erroApi, setErroApi] = useState<string | null>(null);
  const [erroTelefone, setErroTelefone] = useState<string | null>(null);
  const [problemasFoto, setProblemasFoto] = useState<readonly ProblemaFoto[]>([]);

  const [dados, setDados] = useState<DadosFormulario>({
    nome: '',
    sobrenome: '',
    cargo: '',
    telefone: '',
    fotoPreview: null,
    fotoBase64: null,
    fotoRemovida: false,
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    const r = await meuPerfil();
    if (r.ok) {
      setPerfil(r.dados);
      setDados({
        nome: r.dados.nome ?? '',
        sobrenome: r.dados.sobrenome ?? '',
        cargo: r.dados.cargo ?? '',
        telefone: r.dados.telefone ? exibicaoParaCampo(r.dados.telefone) : '',
        fotoPreview: r.dados.fotoUrl,
        fotoBase64: null,
        fotoRemovida: false,
      });
    }
    setCarregando(false);
  }, []);

  useEffect(() => {
    if (sessao === null) return;
    void carregar();
  }, [sessao, carregar]);

  function campoTexto(chave: keyof DadosFormulario, valor: string) {
    setDados((prev) => ({ ...prev, [chave]: valor }));
    setSucesso(false);
  }

  async function aoEscolherFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    if (arquivo === undefined) return;

    const bitmap = await createImageBitmap(arquivo);
    const candidata: FotoCandidata = {
      tipo: arquivo.type,
      tamanhoBytes: arquivo.size,
      largura: bitmap.width,
      altura: bitmap.height,
    };

    const problemas = validarFoto(candidata);
    setProblemasFoto(problemas);
    if (problemas.length > 0) return;

    const dataUrl = await redimensionar(arquivo);
    setDados((prev) => ({
      ...prev,
      fotoPreview: dataUrl,
      fotoBase64: dataUrl,
      fotoRemovida: false,
    }));
    setSucesso(false);
  }

  function removerFoto() {
    setDados((prev) => ({ ...prev, fotoPreview: null, fotoBase64: null, fotoRemovida: true }));
    setProblemasFoto([]);
    setSucesso(false);
  }

  async function salvar() {
    setErroTelefone(null);
    setErroApi(null);
    setSucesso(false);

    /* Validar telefone antes de enviar. */
    if (dados.telefone.trim() !== '') {
      const resultado = normalizarTelefoneBr(dados.telefone);
      if (!resultado.valido) {
        setErroTelefone(resultado.motivo);
        return;
      }
    }

    setSalvando(true);

    const mudancas: Record<string, unknown> = {};
    if (dados.nome !== (perfil?.nome ?? '')) mudancas.nome = dados.nome;
    if (dados.sobrenome !== (perfil?.sobrenome ?? '')) mudancas.sobrenome = dados.sobrenome;
    if (dados.cargo !== (perfil?.cargo ?? '')) mudancas.cargo = dados.cargo;

    const telefoneOriginal = perfil?.telefone ? exibicaoParaCampo(perfil.telefone) : '';
    if (dados.telefone !== telefoneOriginal) mudancas.telefone = dados.telefone;

    if (dados.fotoBase64 !== null) {
      mudancas.fotoBase64 = dados.fotoBase64;
    } else if (dados.fotoRemovida) {
      mudancas.fotoUrl = null;
    }

    try {
      const r = await atualizarMeuPerfil(mudancas);
      if (r.ok) {
        setPerfil(r.dados);
        setDados((prev) => ({
          ...prev,
          telefone: r.dados.telefone ? exibicaoParaCampo(r.dados.telefone) : prev.telefone,
          fotoPreview: r.dados.fotoUrl,
          fotoBase64: null,
          fotoRemovida: false,
        }));
        setSucesso(true);
      } else {
        const d = r.dados as { mensagem?: string };
        setErroApi(d.mensagem ?? `Erro ${String(r.status)}`);
        if (r.status === 422) {
          const dErro = r.dados as { erro?: string; mensagem?: string };
          if (dErro.erro === 'TELEFONE_INVALIDO') {
            setErroTelefone(dErro.mensagem ?? 'Telefone inválido.');
          }
        }
      }
    } catch {
      setErroApi('Falha na comunicação com o servidor.');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-20" role="status" aria-live="polite">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-3 border-gray-200 border-t-brand-accent" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Carregando seu perfil...</p>
        </div>
      </div>
    );
  }

  if (perfil === null) {
    return (
      <p className="py-20 text-center text-sm text-gray-500 dark:text-gray-400">
        Não foi possível carregar o perfil.
      </p>
    );
  }

  const estiloInput =
    'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-blue';
  const estiloLabel = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-brand-blue dark:text-white">Meu Perfil</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Seus dados de contato aparecem na proposta que o cliente recebe.
        </p>
      </div>

      {/* Indicador de cadastro completo/incompleto */}
      {!perfil.cadastroCompleto && (
        <div
          role="alert"
          className="rounded-xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600 p-4 space-y-2"
        >
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            Cadastro incompleto — preencha os campos abaixo para poder publicar propostas.
          </p>
          <ul className="list-inside list-disc text-sm text-amber-700 dark:text-amber-300">
            {(perfil.telefone === null || perfil.telefone === '') && (
              <li>Telefone celular (usado no botão de WhatsApp da proposta)</li>
            )}
            {(perfil.cargo === null || perfil.cargo === '') && (
              <li>Cargo (impresso no card de contato)</li>
            )}
            {(perfil.nome === null || perfil.nome === '') && <li>Nome (assinatura da proposta)</li>}
          </ul>
        </div>
      )}

      {/* Alerta específico para e-mail ausente ou inválido */}
      {(perfil.email === null || perfil.email === '' || !perfil.email.includes('@')) && (
        <div
          role="alert"
          className="rounded-xl border-2 border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-600 p-4 space-y-2"
        >
          <p className="text-sm font-semibold text-red-800 dark:text-red-200">
            E-mail de acesso ausente ou inválido — a proposta não pode ser emitida.
          </p>
          <p className="text-sm text-red-700 dark:text-red-300">
            O e-mail aparece na proposta como contato do responsável. Um administrador precisa
            corrigir o cadastro no Cognito para destravar a publicação.
          </p>
        </div>
      )}

      {perfil.cadastroCompleto && (
        <div className="rounded-xl border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 p-4">
          <p className="text-sm font-semibold text-green-700 dark:text-green-300">
            ✓ Cadastro completo — você pode publicar propostas normalmente.
          </p>
        </div>
      )}

      {sucesso && (
        <div
          role="alert"
          className="rounded-lg border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-700 dark:text-green-300"
        >
          Perfil atualizado com sucesso.
        </div>
      )}

      {erroApi !== null && !erroTelefone && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-4 py-3 text-sm text-red-700 dark:text-red-300"
        >
          {erroApi}
        </div>
      )}

      {/* Campos editáveis */}
      <div className="space-y-5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Dados de contato</h2>

        {/* Foto */}
        <div className="flex items-center gap-4">
          {dados.fotoPreview !== null ? (
            <img
              src={dados.fotoPreview}
              alt="Sua foto"
              className="h-16 w-16 rounded-full object-cover border-2 border-brand-accent"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-blue text-xl font-bold text-white">
              {(dados.nome.charAt(0) + dados.sobrenome.charAt(0)).toUpperCase() || '?'}
            </div>
          )}
          <div className="space-y-2">
            <label
              htmlFor="meu-perfil-foto"
              className="cursor-pointer rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Trocar foto
            </label>
            <input
              id="meu-perfil-foto"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                void aoEscolherFoto(e);
              }}
            />
            {dados.fotoPreview !== null && (
              <button
                type="button"
                onClick={removerFoto}
                className="ml-2 text-xs text-red-600 hover:underline"
              >
                Remover
              </button>
            )}
          </div>
        </div>
        {problemasFoto.length > 0 && (
          <div role="alert" className="text-xs text-red-600">
            {problemasFoto.map((p) => p.mensagem).join('. ')}
          </div>
        )}

        {/* Nome */}
        <div>
          <label htmlFor="meu-perfil-nome" className={estiloLabel}>
            Nome
          </label>
          <input
            id="meu-perfil-nome"
            type="text"
            value={dados.nome}
            onChange={(e) => {
              campoTexto('nome', e.target.value);
            }}
            className={estiloInput}
          />
        </div>

        {/* Sobrenome */}
        <div>
          <label htmlFor="meu-perfil-sobrenome" className={estiloLabel}>
            Sobrenome
          </label>
          <input
            id="meu-perfil-sobrenome"
            type="text"
            value={dados.sobrenome}
            onChange={(e) => {
              campoTexto('sobrenome', e.target.value);
            }}
            className={estiloInput}
          />
        </div>

        {/* Cargo */}
        <div>
          <label htmlFor="meu-perfil-cargo" className={estiloLabel}>
            Cargo
          </label>
          <input
            id="meu-perfil-cargo"
            type="text"
            value={dados.cargo}
            onChange={(e) => {
              campoTexto('cargo', e.target.value);
            }}
            list="cargos-sugeridos"
            className={estiloInput}
          />
          <datalist id="cargos-sugeridos">
            {CARGOS_SUGERIDOS.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            Aparece no card de contato da proposta.
          </p>
        </div>

        {/* Telefone */}
        <div>
          <label htmlFor="meu-perfil-telefone" className={estiloLabel}>
            Telefone celular
          </label>
          <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-brand-blue">
            <span className="select-none pl-3 text-sm text-gray-500 dark:text-gray-400">+55</span>
            <input
              id="meu-perfil-telefone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              maxLength={13}
              value={dados.telefone}
              onChange={(e) => {
                campoTexto('telefone', aplicarMascaraTelefoneBr(e.target.value));
                setErroTelefone(null);
              }}
              placeholder="31 99125-9696"
              className="w-full bg-transparent px-2 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none"
            />
          </div>
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            Celular — aparece no botão de WhatsApp da proposta. Precisa ser celular porque o
            WhatsApp não funciona em telefone fixo.
          </p>
          {erroTelefone !== null && (
            <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
              {erroTelefone}
            </p>
          )}
        </div>

        <button
          type="button"
          disabled={salvando}
          onClick={() => {
            void salvar();
          }}
          className="w-full rounded-lg bg-brand-blue px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-50"
        >
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {/* Campos apenas leitura */}
      <div className="space-y-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          Dados gerenciados por administrador
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Estes campos só podem ser alterados por um administrador na tela de Usuários e Permissões.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">E-mail de acesso</p>
            <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-100">{perfil.email ?? '—'}</p>
            <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
              Aparece como e-mail do responsável na proposta. Para alterar, um administrador precisa
              recriar a conta, pois o e-mail é a identidade no pool de autenticação.
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Perfil de acesso</p>
            <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-100">
              {perfil.perfil !== null ? (NOME_PERFIL[perfil.perfil] ?? perfil.perfil) : 'Nenhum'}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Trilha comercial</p>
            <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-100">
              {perfil.trilhas.join(', ') || 'Nenhuma'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
